import './barcode-subscriber.scss';

import { faBarcode, faCamera, faCircleNotch, faLightbulb, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { first } from 'rxjs/operators';
import { Component } from 'vue-property-decorator';

import { ReactiveVue } from '@skinsoft/reactive-vue';

import { input$ } from '../keyboard';
import { CameraScanner, Reader } from './CameraScanner';
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus';

declare const CAMERA_SCANNER_ENABLE_ON_COMPATIBLE_DEVICE: boolean;
declare const CAMERA_SCANNER_READERS: Reader[];

const CAMERA_PERMISSION_NOT_ALLOWED_ERRORS = ["NotAllowedError"]
const CAMERA_NOT_FOUND_ERRORS = ["NotFoundError"]

@Component({
    props: ['width', 'height', 'embedded', 'eventFilterIdentifier'],
    template: require('./barcode-subscriber.html'),
})
export class BarcodeSubscriber extends ReactiveVue {
    public $refs: {
        innerWrapper: HTMLDivElement;
        video: HTMLVideoElement;
    };

    public eventFilterIdentifier: string;
    public width: number;
    public height: number;
    public embedded: boolean;

    private value: string = null as any;
    private hasSeveralDevices: boolean = false;
    private cameraReady: boolean = false;
    private showWarning: boolean = false;
    private warningMessage: string = ''

    // Icons
    private lightBulbIcon = faLightbulb;
    private barcodeIcon = faBarcode;
    private cameraIcon = faCamera;
    private changeIcon = faSyncAlt;
    private loadingIcon = faCircleNotch;

    get supportsCamera() {
        return (
            CAMERA_SCANNER_ENABLE_ON_COMPATIBLE_DEVICE &&
            navigator &&
            navigator.mediaDevices
        )
    }

    private cameraScanner: CameraScanner = null as any;

    private torchAvailable: boolean = false;

    public created() {
        this.cameraScanner = new CameraScanner(CAMERA_SCANNER_READERS,
            () => {
                this.cameraReady = true;
            },
            () => {
                this.cameraReady = false;
            },
            this.width, this.height);
    }

    public async mounted() {
        if (this.supportsCamera && CAMERA_SCANNER_ENABLE_ON_COMPATIBLE_DEVICE) {

            const cameraPermissionStatus: PermissionState | "UNKNOWN" = await this.getCameraPermissionStatus()
            switch(cameraPermissionStatus) {
                case 'denied':
                    this.warnUser(this.$t("alerts.permissions.camera.permissionDeniedWarning") as string)
                    return
                case 'prompt':
                    this.warnUser(this.$t("alerts.permissions.camera.askForPermission") as string)
                    break
                case 'granted':
                    break
                case "UNKNOWN":
                    // Logger
                    console.warn("Cannot get camera permission status")
                    break
            }
            navigator.mediaDevices.getUserMedia({ video: true })
                .then((mediaStream: MediaStream) => {
                    this.startCameraScanner()
                })
                .catch(err => {
                    if(err.name && CAMERA_PERMISSION_NOT_ALLOWED_ERRORS.includes(err.name)) {
                        // for now there is no possibility to ask for permission in a PWA, 
                        // but if it becomes available in the future, we can ask the permission here 
                        // this.askForCameraPermission()
                        console.log("Camera permission not allowed")
                    } 
                    else if(err.name && CAMERA_NOT_FOUND_ERRORS.includes(err.name)) {
                        // handle camera not foud
                        const alertPayload = {
                            message: this.$t("cameraScanner.errors.cameraNotFound", this.$i18n.locale),
                            variant: "danger"
                        };
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                    }
                })
            
        } else {
            this.subscribeToKeyPress();
        }
    }

    private async getCameraPermissionStatus(): Promise<PermissionState | "UNKNOWN"> {
        try {
            // @ts-ignore
            const cameraPermissionStatus: PermissionStatus = await navigator.permissions.query({ name: 'camera' })
            
            if(cameraPermissionStatus?.state) {
                return cameraPermissionStatus.state
            }
            return "UNKNOWN"
        } catch(error) {
            // Logger
            return "UNKNOWN"
        }
    }

    private async startCameraScanner() {
        this.cameraScanner.pipe(first()).subscribe(async code => {
            code.split('').forEach(char => input$.next(
                {
                    char,
                    inputEventIdentifier: this.eventFilterIdentifier,
                }
            ));
            await this.close();
        });
        await this.cameraScanner.start();
        // Check if torch is available
        const videoStream = this.$refs.video?.srcObject;
        if (videoStream) {
            // @ts-ignore
            const capabilities = videoStream.getTracks()[0].getCapabilities();
            this.torchAvailable = !!capabilities.torch;
        }
        // check if the button to change camera has to be displayed
        this.hasSeveralDevices = this.cameraScanner.hasSeveralDevices;
    }

    public async close() {
        if (this.supportsCamera) {
            await this.cameraScanner.stop();
            this.$emit('close');
        } else {
            this.unsubscribeFromKeyPress();
        }
    }

    public beforeDestroy() {
        this.unsubscribeFromKeyPress();
        this.cameraScanner.resetCallbacks()
        // if we don't stop it manually, camera is still in use in the background
        this.cameraScanner.stop()
    }

    public toggleTorch() {
        this.cameraScanner.toggleTorch();
    }

    public changeCamera() {
        this.cameraScanner.goToNextCameraDevice();
    }

    private subscribeToKeyPress() {
        window.addEventListener('keypress', this.onKeyPress);
    }

    private unsubscribeFromKeyPress() {
        window.removeEventListener('keypress', this.onKeyPress);
    }

    private onKeyPress = (event: KeyboardEvent) => {
        event.preventDefault();
        input$.next({ char: String.fromCharCode(event.which), inputEventIdentifier: this.eventFilterIdentifier });
    };

    private warnUser(message: string) {
        this.showWarning = true
        this.warningMessage = message
    }
}
